import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '.'
import { FeatureLike } from 'ol/Feature'
import router from '@/router'
import { Extent } from 'ol/extent'
import { Actualite } from '@/orrm/model/actualite'
import { InfoReglementation } from '@/orrm/model/info-reglementation'
import { Entity } from '@/orrm/model/entity'
import { ViewType } from '@/interfaces/ViewType'
import { context } from '@/store/modules'
import { MapSingleton } from '@/utils/map'
import { Feature } from 'ol'
import { Geometry } from 'ol/geom'

const _namespace = 'map'

@Module({ dynamic: true, store, namespaced: true, name: _namespace })
export default class MapModule extends VuexModule {
  features: FeatureLike[] = []
  layersData: any[] = []
  resolution = 0
  loading = false
  viewType: ViewType | null = null

  get actualites () {
    return this.features.filter((feature) => feature.get('item') instanceof Actualite)
  }

  get infoReglementations () {
    return this.features.filter((feature) => feature.get('item') instanceof InfoReglementation)
  }

  get entities () {
    return this.features.filter((feature) => feature.get('item') instanceof Entity)
  }

  @Mutation
  setResolution (resolution: number) {
    this.resolution = resolution
  }

  @Mutation
  setLoading (loading: boolean) {
    this.loading = loading
  }

  @Mutation
  setFeatures (features: FeatureLike[]) {
    this.features = features
  }

  @Mutation
  setLayersData (layersData: any[]) {
    this.layersData = layersData
  }

  @Mutation
  setViewType (viewType: ViewType | null) {
    this.viewType = viewType
  }

  @Action({ rawError: true })
  applyViewType (viewType: ViewType | null) {
    if (MapSingleton.map) {
      if (this.viewType && !this.viewType.fixed) {
        MapSingleton.map.removeLayer(this.viewType.layer)
      }
      if (viewType) {
        if (MapSingleton.map.getLayers().getArray().indexOf(viewType.layer) === -1) {
          MapSingleton.map.addLayer(viewType.layer)
        }
      }
    }
    this.setViewType(viewType)
  }

  @Action({ rawError: true })
  showClickedFeatures ({ features = [], layersData = [] }: { features?: FeatureLike[]; layersData?: any[] }) {
    for (const feature of features) {
      const f = feature as Feature<Geometry>
      f.set('persistant', true)
      f.set('focus', true)
      f.changed()
    }
    this.setFeatures(features)
    const featuresData: { title: string; result: string }[] = []
    for (const layerData of layersData) {
      const split = layerData.result.split(/ *?Feature \d+: *?/)
      featuresData.push(...split.map((s: string) => {
        return {
          title: layerData.title,
          result: s,
          rubric: layerData.rubric
        }
      }))
    }
    this.setLayersData(featuresData.filter((featureData: any) =>
      !featureData.result.includes('Search returned no results') && featureData.result.includes('=')
    ))

    const layer = context.activeContext?.getFakeLayer(Entity.layerId)
    if (layer) layer.olLayer.changed()

    if (router.currentRoute.name !== 'InfoSelection') {
      router.push({ name: 'InfoSelection' })
    }
  }

  @Action({ rawError: true })
  focusOnFeatures (features: FeatureLike[]) {
    let globalExtent: Extent = [0, 0, 0, 0]

    if (features.length) {
      globalExtent = features[0].getGeometry()!.getExtent()
    }
    for (const feature of features) {
      const geom = feature.getGeometry()!
      const extent = geom.getExtent()
      if (extent[0] < globalExtent[0]) globalExtent[0] = extent[0]
      if (extent[1] < globalExtent[1]) globalExtent[1] = extent[1]
      if (extent[2] > globalExtent[2]) globalExtent[2] = extent[2]
      if (extent[3] > globalExtent[3]) globalExtent[3] = extent[3]
    }

    if (MapSingleton.map) {
      MapSingleton.map.getView().fit(globalExtent)
    }
  }
}
